import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const auth = {
  register: (data) => api.post('/auth/register', data),
  registerWithImage: (formData) => api.post('/auth/register', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  updateProfile: (formData) => api.put('/auth/update-profile', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getProfile: () => api.get('/auth/profile'),
};

export const products = {
  getAll: (params) => api.get('/products', { params }),
  getFeatured: () => api.get('/products/featured'),
  getById: (id) => api.get(`/products/${id}`),
  create: (formData) => api.post('/products', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/products/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/products/${id}`),
  getImage: (imageId) => `${API_URL}/products/image/${imageId}`,
  like: (id) => api.post(`/products/${id}/like`),
};

export const artists = {
  getAll: () => api.get('/artists'),
  getById: (id) => api.get(`/artists/${id}`),
  getImage: (imageId) => `${API_URL}/artists/image/${imageId}`,
};

export const orders = {
  create: (data) => api.post('/orders/create', data),
  verifyPayment: (data) => api.post('/orders/verify-payment', data),
  getBuyerOrders: () => api.get('/orders/buyer'),
  getSellerOrders: () => api.get('/orders/seller'),
  updateTracking: (id, data) => api.put(`/orders/${id}/tracking`, data),
  updateStatus: (id, data) => api.put(`/orders/${id}/status`, data),
  uploadSellerInvoice: (id, formData) => api.post(`/orders/${id}/seller-invoice`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadInvoice: (orderId) => `${API_URL}/orders/${orderId}/invoice`,
  downloadPackingSlip: (orderId) => `${API_URL}/orders/${orderId}/packing-slip`,
};

export const admin = {
  getPendingArtists: () => api.get('/admin/pending-artists'),
  getAllArtists: () => api.get('/admin/all-artists'),
  approveArtist: (id, data) => api.put(`/admin/approve-artist/${id}`, data),
  deleteArtist: (id) => api.delete(`/admin/delete-artist/${id}`),
  updateArtist: (id, data) => api.put(`/admin/update-artist/${id}`, data),
  extendTrial: (id, days) => api.put(`/admin/extend-trial/${id}?days=${days}`),
  getExpiredArtists: () => api.get('/admin/expired-artists'),
  deactivateExpiredArtists: () => api.post('/admin/deactivate-expired-artists'),
  getAllBuyers: () => api.get('/admin/all-buyers'),
  deleteBuyer: (id) => api.delete(`/admin/delete-buyer/${id}`),
  getPendingProducts: () => api.get('/admin/pending-products'),
  getAllProducts: () => api.get('/admin/all-products'),
  approveProduct: (id, data) => api.put(`/admin/approve-product/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/delete-product/${id}`),
  updateProduct: (id, data) => api.put(`/admin/update-product/${id}`, data),
  getAnalytics: () => api.get('/admin/analytics'),
  getAllOrders: () => api.get('/admin/all-orders'),
  generatePackingSlip: (orderId) => api.post(`/admin/orders/${orderId}/packing-slip`),
  // Featured Products Management
  getFeaturedProducts: () => api.get('/admin/featured-products'),
  toggleFeatured: (productId) => api.put(`/admin/featured-products/${productId}`),
  reorderFeatured: (products) => api.put('/admin/featured-products/reorder', { products }),
};

export default api;